import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import CodeOutlined from '@ant-design/icons/CodeOutlined';
import ControlOutlined from '@ant-design/icons/ControlOutlined';
import DatabaseOutlined from '@ant-design/icons/DatabaseOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import RobotOutlined from '@ant-design/icons/RobotOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import ToolOutlined from '@ant-design/icons/ToolOutlined';
import Alert from 'antd/es/alert';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Drawer from 'antd/es/drawer';
import Empty from 'antd/es/empty';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Select from 'antd/es/select';
import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import type { ExecutionConfig, LLMConfig, ToolConfig } from '@mai/types';

import { ConfigurationEditor } from './ConfigurationEditor';

import { AgentExecutionLiveTail } from '@/components/AgentModals';
import { RunAgentModal } from '@/components/AgentModals';
import { CenterSpin } from '@components/CenterSpin';
import {
  useAgentConfigurationVersionsByAgentId,
  useCreateAgentConfigurationVersion,
  useUpdateAgentConfigurationVersion,
} from '@queries/agent-configuration-versions';
import { useAgentById } from '@queries/agents';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Extract just the configuration fields we need
interface AgentConfiguration {
  instructions: string;
  inputSchema: Record<string, unknown>;
  outputSchema: Record<string, unknown>;
  toolConfig: ToolConfig;
  executionConfig: ExecutionConfig;
  llmConfig: LLMConfig;
  systemPrompt: string | null;
}

const PageContainer = styled.div`
  height: calc(
    100vh - ${({ theme }) => theme.controlHeightLG * 1.25}px -
      ${({ theme }) => theme.padding * 4}px
  );
  display: flex;
  flex-direction: column;
  margin: -${({ theme }) => theme.padding * 2}px;
`;

const HeaderContainer = styled.div`
  ${({ theme }) => `
  padding: ${theme.padding}px ${theme.paddingLG}px;
  border-bottom: ${`${theme.lineWidth}px solid ${theme.colorBorderSecondary}`};
  flex-shrink: 0;

  @media (max-width: ${theme.screenMD}px) {
    padding: ${theme.paddingSM}px ${theme.padding}px;
  }
`}
`;

const BackButton = styled(Button)`
  ${({ theme }) => `
  margin-bottom: ${theme.marginXS}px;
`}
`;

const TitleContainer = styled.div`
  ${({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.marginXL}px;
  flex-wrap: wrap;

  @media (max-width: ${theme.screenMD}px) {
    gap: ${theme.margin}px;
  }
`}
`;

const TitleSection = styled.div`
  ${({ theme }) => `
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${theme.margin}px;
  min-width: 0;

  @media (max-width: ${theme.screenSM}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.marginXS}px;
  }
`}
`;

const VersionSelect = styled(Select)`
  ${({ theme }) => `
  .ant-select-selector {
    padding-right: ${theme.paddingXS}px !important;
  }

  @media (max-width: ${theme.screenSM}px) {
    width: 100%;
  }
`}
`;

const ActionButtons = styled.div`
  ${({ theme }) => `
  display: flex;
  gap: ${theme.marginXS}px;
  flex-shrink: 0;

  @media (max-width: ${theme.screenSM}px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`}
`;

const WorkbenchContainer = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  min-height: 0; // Important for flexbox children with overflow
`;

const TwoColumnLayout = styled.div`
  display: flex;
  height: 100%;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.screenMD}px) {
    flex-direction: column;
  }
`;

const InstructionsColumn = styled.div`
  ${({ theme }) => `
  flex: 1;
  display: flex;
  flex-direction: column;
  border-right: ${`${theme.lineWidth}px solid ${theme.colorBorderSecondary}`};
  overflow: hidden;
  min-width: 0;

  @media (max-width: ${theme.screenMD}px) {
    border-right: none;
    border-bottom: ${`${theme.lineWidth}px solid ${theme.colorBorderSecondary}`};
    flex: 0 0 50%;
    min-height: ${theme.controlHeightLG * 10}px;
  }
`}
`;

const ExecutionColumn = styled.div`
  ${({ theme }) => `
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;

  @media (max-width: ${theme.screenMD}px) {
    flex: 1;
  }
`}
`;

const ConfigurationHeader = styled.div`
  ${({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.paddingSM}px ${theme.padding}px;
  border-bottom: ${`${theme.lineWidth}px solid ${theme.colorBorderSecondary}`};
  background: ${theme.colorBgContainer};
  flex-shrink: 0;
  min-height: ${theme.controlHeight}px;

  @media (max-width: ${theme.screenSM}px) {
    padding: ${theme.paddingXS}px ${theme.paddingSM}px;
    min-height: ${theme.controlHeight}px;
  }
`}
`;

const ConfigurationTitle = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSize}px;
  font-weight: ${theme.fontWeightStrong};
  line-height: ${theme.lineHeight};
`}
`;

const ConfigurationActions = styled.div`
  ${({ theme }) => `
  display: flex;
  gap: ${theme.marginXXS}px;

  @media (max-width: ${theme.screenSM}px) {
    flex-wrap: wrap;
    max-width: 88px;
  }
`}
`;

const IconButton = styled(Button)`
  ${({ theme }) => `
  min-width: ${theme.controlHeightSM}px;
  min-height: ${theme.controlHeightSM}px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:not(:hover) {
    background: transparent;
    border-color: transparent;
  }

  @media (max-width: ${theme.screenSM}px) {
    min-width: ${theme.controlHeight}px;
    min-height: ${theme.controlHeight}px;
  }
`}
`;

const InstructionsEditor = styled.div`
  ${({ theme }) => `
  flex: 1;
  padding: ${theme.padding}px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: ${theme.screenSM}px) {
    padding: ${theme.paddingSM}px;
  }

  /* Make Form.Item wrapper divs fill height and width */
  .instructions-form-item {
    .ant-form-item-row {
      height: 100%;
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    .ant-form-item-control {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    .ant-form-item-control-input {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    .ant-form-item-control-input-content {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
    }
  }
`}
`;

const InstructionsTextArea = styled(TextArea)<{ $isDirty: boolean }>`
  ${({ theme, $isDirty }) => `
  font-family: ${theme.fontFamilyCode};
  font-size: ${theme.fontSize}px;
  border-color: ${$isDirty ? theme.colorPrimary : theme.colorBorder} !important;
  transition: border-color ${theme.motionDurationMid} ease;
  height: 100% !important;
  width: 100% !important;
  min-height: 0; /* Allow shrinking */
  resize: none !important; /* Disable resize handle */

  &:hover {
    border-color: ${$isDirty ? theme.colorPrimary : theme.colorBorderSecondary} !important;
  }

  &:focus {
    border-color: ${theme.colorPrimary} !important;
    box-shadow: 0 0 0 ${theme.lineWidthFocus}px ${theme.colorPrimaryBg};
  }
`}
`;

const ExecutionHeader = styled.div`
  ${({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.marginXS}px;
  padding: ${theme.paddingSM}px ${theme.padding}px;
  border-bottom: ${`${theme.lineWidth}px solid ${theme.colorBorderSecondary}`};
  background: ${theme.colorBgContainer};
  flex-shrink: 0;
  min-height: ${theme.controlHeight}px;

  @media (max-width: ${theme.screenSM}px) {
    padding: ${theme.paddingXS}px ${theme.paddingSM}px;
    min-height: ${theme.controlHeight}px;
  }
`}
`;

const ExecutionContent = styled.div`
  ${({ theme }) => `
  flex: 1;
  padding: ${theme.padding}px;
  overflow-y: auto;
  overflow-x: hidden;

  @media (max-width: ${theme.screenSM}px) {
    padding: ${theme.paddingSM}px;
  }
`}
`;

const PageTitle = styled(Title)`
  ${({ theme }) => `
  margin: 0 !important;
  font-weight: ${theme.fontWeightStrong} !important;
  font-size: ${theme.fontSizeHeading3}px !important;

  @media (max-width: ${theme.screenMD}px) {
    font-size: ${theme.fontSizeHeading4}px !important;
  }
`}
`;

const VersionTag = styled(Tag)`
  ${({ theme }) => `
  margin: 0 !important;
  font-size: ${theme.fontSizeSM}px !important;
`}
`;

const DrawerContent = styled.div`
  ${({ theme }) => `
  padding: ${theme.paddingSM}px 0;
`}
`;

const ModalContent = styled.div`
  ${({ theme }) => `
  .modal-text {
    display: block;
    margin-bottom: ${theme.marginLG}px;
  }
  
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: ${theme.marginXS}px;
  }
`}
`;

export const AgentWorkbenchPage: React.FC = () => {
  const { teamId, agentId } = useParams<{
    teamId: string;
    agentId: string;
  }>();
  const { message, modal } = App.useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const theme = useTheme();

  // UI state only - no derived state
  const [showRunModal, setShowRunModal] = useState(false);

  // Simple dirty state tracking for save button visual feedback only
  const [isDirty, setIsDirty] = useState(false);

  // Drawer states for configuration panels
  const [drawerConfig, setDrawerConfig] = useState<{
    open: boolean;
    type: 'execution' | 'llm' | 'tools' | 'schemas' | null;
  }>({ open: false, type: null });

  // Get version and execution ID from query params
  const workingVersionId = searchParams.get('version');
  const currentExecutionId = searchParams.get('execution');

  const { data: agent, isLoading: isLoadingAgent } = useAgentById(
    agentId || null,
  );
  const { data: configVersions, isLoading: isLoadingVersions } =
    useAgentConfigurationVersionsByAgentId(agentId || null);

  const createVersionMutation = useCreateAgentConfigurationVersion();
  const updateVersionMutation = useUpdateAgentConfigurationVersion();

  // Derived state - no useEffect needed
  const sortedVersions = useMemo(() => {
    if (!configVersions) return [];
    return [...configVersions].sort(
      (a, b) => b.versionNumber - a.versionNumber,
    );
  }, [configVersions]);

  const currentVersion = configVersions?.find((v) => v.isCurrent);
  const latestVersion = sortedVersions[0];

  const workingVersion = useMemo(() => {
    if (workingVersionId && configVersions) {
      return configVersions.find(
        (v) => v.agentConfigurationVersionId === workingVersionId,
      );
    }
    return latestVersion;
  }, [workingVersionId, configVersions, latestVersion]);

  const isWorkingOnDraft = workingVersion ? !workingVersion.isCurrent : false;

  // Initialize form values when version changes
  useEffect(() => {
    if (workingVersion) {
      form.setFieldsValue({
        instructions: workingVersion.instructions || '',
      });
      // Reset dirty state when switching versions
      setIsDirty(false);
    }
  }, [workingVersion, form]);

  // Get current config - stable reference to prevent unnecessary re-renders
  const currentConfig = useMemo(() => {
    if (!workingVersion) return null;

    // Return the working version directly to maintain stable reference
    // Only create new object if we need to modify it
    return workingVersion;
  }, [workingVersion]);

  // Simple form change handler for visual feedback
  const handleInstructionsChange = useDebouncedCallback(() => {
    setIsDirty(true);
  }, 300);

  // Handle drawer open/close
  const openDrawer = useCallback(
    (type: 'execution' | 'llm' | 'tools' | 'schemas') => {
      setDrawerConfig({ open: true, type });
    },
    [],
  );

  const closeDrawer = useCallback(() => {
    setDrawerConfig({ open: false, type: null });
  }, []);

  // Responsive drawer width with proper dependencies
  const [drawerWidth, setDrawerWidth] = useState<string | number>(480);

  useEffect(() => {
    const updateDrawerWidth = () => {
      if (window.innerWidth < 576) {
        setDrawerWidth('100%');
      } else if (window.innerWidth < 768) {
        setDrawerWidth(400);
      } else if (window.innerWidth < 1200) {
        setDrawerWidth(480);
      } else {
        setDrawerWidth(520);
      }
    };

    updateDrawerWidth();
    window.addEventListener('resize', updateDrawerWidth);
    return () => window.removeEventListener('resize', updateDrawerWidth);
  }, []);

  // Clear dirty state helper
  const clearDirtyState = useCallback(() => {
    setIsDirty(false);
  }, []);

  // User actions only - no automatic triggers
  const handleVersionChange = useCallback(
    (versionId: string) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('version', versionId);
        return newParams;
      });
      // Reset form when version changes and clear dirty state
      form.resetFields();
      clearDirtyState();
    },
    [setSearchParams, form, clearDirtyState],
  );

  const handleConfigChange = useCallback(
    (_updates: Partial<AgentConfiguration>) => {
      // This is called for schema and tool updates from modals
      setIsDirty(true);
    },
    [],
  );

  // Efficient change validation - only checks the instructions field
  const validateActualChanges = useCallback(() => {
    if (!workingVersion) return false;

    // Get current instructions value
    const currentInstructions = form.getFieldValue('instructions');

    // Compare instructions with the working version
    return currentInstructions !== workingVersion.instructions;
  }, [form, workingVersion]);

  const handleRunAgent = () => {
    if (!currentConfig || !currentVersion) {
      void message.error('No configuration available');
      return;
    }

    // Check for actual changes only when needed
    const hasActualChanges = validateActualChanges();

    if (hasActualChanges) {
      void modal.confirm({
        title: 'Unsaved Changes',
        content:
          'You have unsaved configuration changes. Please save your changes before running the agent.',
        okText: 'Save & Run',
        cancelText: 'Cancel',
        onOk: async () => {
          // Save first, then run
          try {
            if (workingVersion) {
              if (workingVersion.isCurrent) {
                await handleUpdateActive();
              } else {
                await handleSaveDraft();
              }
            }
            setShowRunModal(true);
          } catch (error) {
            // Error handling is already in the save functions
          }
        },
        onCancel: () => {
          // User cancelled - don't open the run modal
          return;
        },
      });
      return;
    }

    setShowRunModal(true);
  };



  const handleSaveConfig = () => {
    if (!workingVersion) return;

    // Validate if there are actual changes before saving
    const hasActualChanges = validateActualChanges();

    if (!hasActualChanges) {
      // Clear false positive and show message
      clearDirtyState();
      void message.info('No changes to save');
      return;
    }

    // If editing an active version, ask user what to do
    if (workingVersion.isCurrent) {
      const modalInstance = modal.info({
        title: 'Editing Active Version',
        content: (
          <ModalContent>
            <Text className="modal-text">
              You are editing the active configuration. How would you like to
              save your changes?
            </Text>
            <div className="modal-actions">
              <Button onClick={() => modalInstance.destroy()}>Cancel</Button>
              <Button
                onClick={() => {
                  modalInstance.destroy();
                  const hideLoading = message.loading(
                    'Creating new draft version...',
                    0,
                  );
                  void handleCreateNewDraft().finally(() => hideLoading());
                }}
              >
                Save as New Draft
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  modalInstance.destroy();
                  const hideLoading = message.loading(
                    'Updating active configuration...',
                    0,
                  );
                  void handleUpdateActive().finally(() => hideLoading());
                }}
              >
                Update Active
              </Button>
            </div>
          </ModalContent>
        ),
        closable: true,
        maskClosable: false,
        okButtonProps: { style: { display: 'none' } },
        footer: null,
      });
      return;
    }

    // Editing a draft, just save it
    const hideLoading = message.loading('Saving draft...', 0);
    void handleSaveDraft().finally(() => hideLoading());
  };

  const handleUpdateActive = async () => {
    if (!workingVersion || !currentConfig) return;

    try {
      await updateVersionMutation.mutateAsync({
        versionId: workingVersion.agentConfigurationVersionId,
        data: {
          instructions: form.getFieldValue('instructions'),
          inputSchema: currentConfig.inputSchema,
          outputSchema: currentConfig.outputSchema,
          toolConfig: currentConfig.toolConfig,
          executionConfig: currentConfig.executionConfig,
          llmConfig: currentConfig.llmConfig,
          systemPrompt: currentConfig.systemPrompt,
          isCurrent: true,
        },
      });

      void message.success('Active configuration updated');
      clearDirtyState(); // Clear dirty state after successful save
    } catch (error) {
      void message.error('Failed to update configuration');
    }
  };

  const handleCreateNewDraft = async () => {
    if (!workingVersion || !currentConfig || !agentId) return;

    try {
      const newVersion = await createVersionMutation.mutateAsync({
        agentId,
        instructions: form.getFieldValue('instructions'),
        inputSchema: currentConfig.inputSchema,
        outputSchema: currentConfig.outputSchema,
        toolConfig: currentConfig.toolConfig,
        executionConfig: currentConfig.executionConfig,
        llmConfig: currentConfig.llmConfig,
        systemPrompt: currentConfig.systemPrompt,
        versionNumber: workingVersion.versionNumber + 1,
        isCurrent: false,
      });

      handleVersionChange(newVersion.agentConfigurationVersionId);
      void message.success(`Created draft version ${newVersion.versionNumber}`);
      // Note: clearDirtyState() is called in handleVersionChange
    } catch (error) {
      void message.error('Failed to create draft version');
    }
  };

  const handleSaveDraft = async () => {
    if (!workingVersion || !currentConfig) return;

    try {
      await updateVersionMutation.mutateAsync({
        versionId: workingVersion.agentConfigurationVersionId,
        data: {
          instructions: form.getFieldValue('instructions'),
          inputSchema: currentConfig.inputSchema,
          outputSchema: currentConfig.outputSchema,
          toolConfig: currentConfig.toolConfig,
          executionConfig: currentConfig.executionConfig,
          llmConfig: currentConfig.llmConfig,
          systemPrompt: currentConfig.systemPrompt,
        },
      });

      void message.success('Draft saved successfully');
      clearDirtyState(); // Clear dirty state after successful save
    } catch (error) {
      void message.error('Failed to save draft');
    }
  };

  const handlePublishConfig = async () => {
    if (!workingVersion || !currentConfig || workingVersion.isCurrent) return;

    // Get current form values
    const formData = form.getFieldsValue();

    const hideLoading = message.loading('Publishing configuration...', 0);

    try {
      await updateVersionMutation.mutateAsync({
        versionId: workingVersion.agentConfigurationVersionId,
        data: {
          instructions: form.getFieldValue('instructions'),
          inputSchema: currentConfig.inputSchema,
          outputSchema: currentConfig.outputSchema,
          toolConfig: currentConfig.toolConfig,
          executionConfig:
            formData.executionConfig ?? currentConfig.executionConfig,
          llmConfig: formData.llmConfig ?? currentConfig.llmConfig,
          systemPrompt: formData.systemPrompt ?? currentConfig.systemPrompt,
          isCurrent: true,
        },
      });

      void message.success(
        `Version ${workingVersion.versionNumber} is now active`,
      );
      clearDirtyState(); // Clear dirty state after successful publish
    } catch (error) {
      void message.error('Failed to publish configuration');
    } finally {
      hideLoading();
    }
  };

  if (isLoadingAgent || isLoadingVersions) {
    return <CenterSpin />;
  }

  if (!agent || !currentVersion) {
    return (
      <PageContainer>
        <Alert
          message="Agent not found"
          description="The requested agent could not be found or has no configuration."
          type="error"
          showIcon
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderContainer>
        <Link to={`/team/${teamId}/agents/${agentId}`}>
          <BackButton type="text" icon={<ArrowLeftOutlined />}>
            Back to Agent
          </BackButton>
        </Link>

        <TitleContainer>
          <TitleSection>
            <PageTitle level={3}>
              {agent.name}
              {isDirty ? ' *' : ''}
            </PageTitle>
            {workingVersion && (
              <VersionSelect
                value={workingVersion.agentConfigurationVersionId}
                onChange={(value) => handleVersionChange(value as string)}
                variant="borderless"
                size="small"
              >
                {sortedVersions.map((version) => (
                  <Select.Option
                    key={version.agentConfigurationVersionId}
                    value={version.agentConfigurationVersionId}
                  >
                    v{version.versionNumber}{' '}
                    {version.isCurrent && (
                      <VersionTag color="blue">Active</VersionTag>
                    )}
                  </Select.Option>
                ))}
              </VersionSelect>
            )}
          </TitleSection>

          <ActionButtons>
            {isWorkingOnDraft && (
              <Button onClick={handlePublishConfig}>Make Active</Button>
            )}
            <Button
              icon={<SaveOutlined />}
              onClick={handleSaveConfig}
              disabled={!isDirty}
              type={isDirty ? 'primary' : 'default'}
            >
              Save{isDirty ? ' *' : ''}
            </Button>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleRunAgent}
              disabled={!currentConfig}
            >
              Run
            </Button>
          </ActionButtons>
        </TitleContainer>
      </HeaderContainer>

      <WorkbenchContainer>
        <TwoColumnLayout>
          <InstructionsColumn>
            <ConfigurationHeader>
              <ConfigurationTitle>Agent Instructions</ConfigurationTitle>
              <ConfigurationActions>
                <Tooltip title="Execution Settings" placement="bottom">
                  <IconButton
                    icon={<ControlOutlined />}
                    onClick={() => openDrawer('execution')}
                  />
                </Tooltip>
                <Tooltip title="Language Model" placement="bottom">
                  <IconButton
                    icon={<RobotOutlined />}
                    onClick={() => openDrawer('llm')}
                  />
                </Tooltip>
                <Tooltip title="Tools & Capabilities" placement="bottom">
                  <IconButton
                    icon={<ToolOutlined />}
                    onClick={() => openDrawer('tools')}
                  />
                </Tooltip>
                <Tooltip title="Input/Output Schemas" placement="bottom">
                  <IconButton
                    icon={<DatabaseOutlined />}
                    onClick={() => openDrawer('schemas')}
                  />
                </Tooltip>
              </ConfigurationActions>
            </ConfigurationHeader>

            <InstructionsEditor>
              <Form
                form={form}
                onFieldsChange={handleInstructionsChange}
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Form.Item
                  name="instructions"
                  rules={[
                    {
                      required: true,
                      message: 'Please provide agent instructions',
                    },
                  ]}
                  style={{
                    marginBottom: 0,
                    flex: 1,
                    display: 'flex',
                    width: '100%',
                  }}
                  className="instructions-form-item"
                >
                  <InstructionsTextArea
                    placeholder="Enter the system instructions for the agent..."
                    $isDirty={isDirty}
                    autoSize={false}
                  />
                </Form.Item>
              </Form>
            </InstructionsEditor>
          </InstructionsColumn>

          <ExecutionColumn>
            <ExecutionHeader>
              <CodeOutlined
                style={{
                  fontSize: `${theme.fontSizeLG}px`,
                  lineHeight: `${theme.lineHeightLG - 0.5}px`,
                }}
              />
              <ConfigurationTitle>Execution Timeline</ConfigurationTitle>
            </ExecutionHeader>

            <ExecutionContent>
              {currentExecutionId ? (
                <AgentExecutionLiveTail
                  agentExecutionId={currentExecutionId}
                  defaultStepTypes={[
                    'thought',
                    'code',
                    'final_answer',
                    'observation',
                    'plan',
                    'fact',
                  ]}
                />
              ) : (
                <Empty
                  description="Click 'Run' to start a test execution"
                  style={{ marginTop: `${theme.marginXXL}px` }}
                />
              )}
            </ExecutionContent>
          </ExecutionColumn>
        </TwoColumnLayout>
      </WorkbenchContainer>

      <Drawer
        title={
          drawerConfig.type === 'execution'
            ? 'Execution Settings'
            : drawerConfig.type === 'llm'
              ? 'Language Model'
              : drawerConfig.type === 'tools'
                ? 'Tools & Capabilities'
                : drawerConfig.type === 'schemas'
                  ? 'Input/Output Schemas'
                  : ''
        }
        placement="right"
        onClose={closeDrawer}
        open={drawerConfig.open}
        width={drawerWidth}
      >
        <DrawerContent>
          {currentConfig && drawerConfig.type && (
            <ConfigurationEditor
              config={currentConfig}
              versionId={workingVersion?.agentConfigurationVersionId}
              form={form}
              onChange={handleConfigChange}
              activeSection={drawerConfig.type}
            />
          )}
        </DrawerContent>
      </Drawer>

      <RunAgentModal
        open={showRunModal}
        onClose={() => setShowRunModal(false)}
        agentId={agentId || ''}
        configurationVersion={workingVersion || currentVersion}
        teamId={teamId}
        shouldNavigateToExecutions={false}
        onExecutionCreated={(executionId: string) => {
          // Set execution ID in search params for persistence on refresh
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set('execution', executionId);
            return newParams;
          });
        }}
      />
    </PageContainer>
  );
};
